import React, { useState, useEffect } from 'react';
import { progressAPI } from '../services/api';
import { FaTimes, FaCheck, FaEdit, FaSave, FaArrowRight, FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa';
import '../styles/ExamReview.css';

const ExamReviewModal = ({ courseId, studentId, studentName, onClose }) => {
    const [exams, setExams] = useState([]);
    const [loadingExams, setLoadingExams] = useState(true);
    const [selectedAttemptId, setSelectedAttemptId] = useState(null);
    const [attemptDetails, setAttemptDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // For editing grades
    const [editingAnswerId, setEditingAnswerId] = useState(null);
    const [editPoints, setEditPoints] = useState('');
    const [savingGrade, setSavingGrade] = useState(false);

    useEffect(() => {
        fetchExams();
    }, [studentId]);

    const fetchExams = async () => {
        try {
            const { data } = await progressAPI.getStudentExams(courseId, studentId);
            setExams(data);
        } catch (error) {
            console.error('Failed to fetch exams:', error);
        } finally {
            setLoadingExams(false);
        }
    };

    const handleSelectExam = async (attemptId) => {
        setSelectedAttemptId(attemptId);
        setLoadingDetails(true);
        try {
            const { data } = await progressAPI.getAttemptDetails(attemptId);
            setAttemptDetails(data);
        } catch (error) {
            console.error('Failed to fetch details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleBackToList = () => {
        setSelectedAttemptId(null);
        setAttemptDetails(null);
        fetchExams(); // Refresh to get updated status
    };

    const handleSaveGrade = async (answerId, maxPoints) => {
        if (editPoints === '' || isNaN(editPoints)) return alert('الرجاء إدخال علامة صحيحة');
        const points = parseFloat(editPoints);
        if (points < 0 || points > parseFloat(maxPoints)) return alert(`العلامة يجب أن تكون بين 0 و ${maxPoints}`);

        setSavingGrade(true);
        try {
            await progressAPI.updateAnswerGrade(selectedAttemptId, answerId, points);
            // Update local state smoothly
            setAttemptDetails(prev => ({
                ...prev,
                answers: prev.answers.map(ans =>
                    ans.id === answerId
                        ? { ...ans, points_earned: points, needs_review: 0, is_correct: points > 0 ? 1 : 0 }
                        : ans
                )
            }));
            setEditingAnswerId(null);
        } catch (error) {
            console.error('Failed to save grade:', error);
            alert('حدث خطأ أثناء حفظ التقييم');
        } finally {
            setSavingGrade(false);
        }
    };

    const renderExamsList = () => {
        if (loadingExams) return <div className="er-loader">جاري تحميل امتحانات الطالب...</div>;
        if (exams.length === 0) return <div className="er-empty">لم يقم هذا الطالب بإجراء أي امتحانات في هذه الدورة حتى الآن.</div>;

        return (
            <div className="er-exams-list">
                {exams.map(exam => (
                    <div key={exam.id} className="er-exam-card" onClick={() => handleSelectExam(exam.id)}>
                        <div className="er-exam-info">
                            <h4>{exam.exam_title}</h4>
                            <span className="er-exam-date">{new Date(exam.submitted_at).toLocaleString('ar-EG')}</span>
                        </div>
                        <div className="er-exam-status">
                            {exam.status === 'needs_review' ? (
                                <span className="er-badge warning"><FaExclamationTriangle /> بحاجة لتصحيح</span>
                            ) : (
                                <span className={`er-badge ${exam.passed ? 'success' : 'danger'}`}>
                                    {exam.passed ? 'ناجح' : 'راسب'} ({parseFloat(exam.percent).toFixed(1)}%)
                                </span>
                            )}
                            <div className="er-exam-score">
                                <strong>{parseFloat(exam.score).toFixed(1)}</strong> / {parseFloat(exam.max_score).toFixed(1)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const getAiReasonText = (code) => {
        switch (code) {
            case 0: return 'إجابة مثالية ومكتملة';
            case 1: return 'إجابة صحيحة ولكن مختصرة';
            case 2: return 'إجابة صحيحة جزئياً';
            case 3: return 'ينقصها نقطة رئيسية هامة';
            case 4: return 'إجابة خارج سياق الموضوع';
            case 5: return 'إجابة غير كافية للتقييم أو فارغة';
            default: return 'تم التقييم';
        }
    };

    const renderAttemptDetails = () => {
        if (loadingDetails) return <div className="er-loader">جاري تحميل الإجابات...</div>;
        if (!attemptDetails) return null;

        const { attempt, answers } = attemptDetails;

        return (
            <div className="er-details-view">
                <div className="er-details-header">
                    <button className="er-back-btn" onClick={handleBackToList}>
                        <FaArrowRight /> رجوع لقائمة الامتحانات
                    </button>
                    <div className="er-attempt-summary">
                        <h3>{attempt.exam_title}</h3>
                        <div className="er-summary-stats">
                            <span>النتيجة الحالية: <strong>{parseFloat(attempt.score).toFixed(1)} / {parseFloat(attempt.max_score).toFixed(1)}</strong></span>
                            {attempt.status === 'needs_review' && <span className="er-badge warning">بحاجة لتصحيح</span>}
                        </div>
                    </div>
                </div>

                <div className="er-answers-list">
                    {answers.map((ans, idx) => (
                        <div key={ans.id} className={`er-answer-card ${ans.needs_review ? 'er-needs-review' : ''}`}>
                            <div className="er-q-header">
                                <span className="er-q-num">سؤال {idx + 1}</span>
                                <span className="er-q-type">{ans.q_type === 'essay' ? 'سؤال مقالي' : 'مؤتمت'}</span>
                                <span className="er-q-points">الدرجة العظمى: {parseFloat(ans.points).toFixed(1)}</span>
                            </div>
                            <div className="er-q-text">{ans.question_text}</div>

                            <div className="er-student-answer">
                                <span className="er-label">إجابة الطالب:</span>
                                <p className="er-answer-text">{ans.answer_text || (ans.selected_option_id ? 'خيار محدد (من الاختيارات)' : <em style={{ color: '#999' }}>لم يجب الطالب</em>)}</p>
                            </div>

                            <div className="er-evaluation">
                                {ans.q_type === 'essay' && ans.grading_source && (
                                    <div className="er-grading-source-info" style={{ marginBottom: '10px' }}>
                                        {ans.grading_source === 'ai' && (
                                            <div className="er-ai-feedback" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <span className="er-badge ai" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', width: 'fit-content' }}>🤖 تم التقييم بالذكاء الاصطناعي</span>
                                                {ans.ai_reason_code !== null && (
                                                    <span className="er-ai-reason" style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                                                        <strong>سبب التقييم: </strong> {getAiReasonText(ans.ai_reason_code)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {ans.grading_source === 'manual_override' && (
                                            <span className="er-badge info" style={{ backgroundColor: '#fef08a', color: '#854d0e', width: 'fit-content' }}>
                                                👨‍🏫 تم تعديل الدرجة يدوياً (الدرجة المقترحة من AI أصلًا: {ans.ai_score !== null ? parseFloat(ans.ai_score).toFixed(1) : 'غير متوفر'})
                                            </span>
                                        )}
                                        {ans.grading_source === 'manual' && (
                                            <span className="er-badge info" style={{ backgroundColor: '#fef08a', color: '#854d0e', width: 'fit-content' }}>
                                                👨‍🏫 تم التصحيح يدوياً
                                            </span>
                                        )}
                                    </div>
                                )}
                                {editingAnswerId === ans.id ? (
                                    <div className="er-grade-editor">
                                        <input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            max={ans.points}
                                            value={editPoints}
                                            onChange={(e) => setEditPoints(e.target.value)}
                                            placeholder={`أدخل درجة من ${ans.points}`}
                                            autoFocus
                                        />
                                        <button
                                            className="er-save-btn"
                                            onClick={() => handleSaveGrade(ans.id, ans.points)}
                                            disabled={savingGrade}
                                        >
                                            {savingGrade ? 'جاري الحفظ...' : <><FaSave /> حفظ التقييم</>}
                                        </button>
                                        <button className="er-cancel-btn" onClick={() => setEditingAnswerId(null)}>إلغاء</button>
                                    </div>
                                ) : (
                                    <div className="er-grade-display">
                                        <div className={`er-grade-box ${ans.points_earned > 0 ? 'good' : 'bad'}`}>
                                            درجة الطالب: <strong>{parseFloat(ans.points_earned || 0).toFixed(1)}</strong>
                                        </div>
                                        {ans.q_type === 'essay' && (
                                            <button
                                                className="er-edit-btn"
                                                onClick={() => {
                                                    setEditingAnswerId(ans.id);
                                                    setEditPoints(ans.points_earned !== null ? parseFloat(ans.points_earned) : '');
                                                }}
                                            >
                                                <FaEdit /> {ans.needs_review ? 'صحح الآن' : 'تعديل الدرجة'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="er-details-footer" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button 
                        className="er-submit-final-btn" 
                        onClick={onClose}
                    >
                        <FaPaperPlane /> إرسال النتيجة للطالب وإغلاق
                    </button>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '8px' }}>
                        سيتم حفظ التعديلات وإشعار الطالب بنتيجته النهائية
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="er-modal-overlay">
            <div className="er-modal-content">
                <div className="er-modal-header">
                    <h2>مراجعة امتحانات الطالب: <span className="er-student-name">{studentName}</span></h2>
                    <button className="er-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="er-modal-body">
                    {selectedAttemptId ? renderAttemptDetails() : renderExamsList()}
                </div>
            </div>
        </div>
    );
};

export default ExamReviewModal;
